import { useEffect } from 'react';

/** Probe a same-origin 3D HTML iframe for a camera (Three.js or Cadwork WebViewer). */

export interface HtmlViewerVec3 {
  x: number;
  y: number;
  z: number;
}

export interface HtmlViewerCameraPose {
  position: HtmlViewerVec3;
  target: HtmlViewerVec3;
  zoom?: number;
  fov?: number;
}

export interface HtmlViewerOrbit {
  yaw: number;
  pitch: number;
  distance: number;
  fov?: number;
  zoom?: number;
}

type Vec3Like = HtmlViewerVec3 & { set?: (x: number, y: number, z: number) => void };

export interface HtmlViewerCameraBinding {
  camera?: { position: Vec3Like; zoom?: number; up?: Vec3Like; updateProjectionMatrix?: () => void };
  controls?: {
    target: Vec3Like;
    update?: () => void;
    addEventListener?: (type: string, fn: () => void) => void;
    removeEventListener?: (type: string, fn: () => void) => void;
  };
  readPose?: () => HtmlViewerCameraPose | null;
  applyPose?: (pose: HtmlViewerCameraPose) => void;
  up?: 'y' | 'z';
}

type CadworkCamera = {
  getCameraPosition?: () => unknown;
  getCameraTarget?: () => unknown;
  getCameraUpVector?: () => unknown;
  getCameraUpVectorRef?: () => unknown;
  getFieldOfView?: () => number;
  setCameraPositionAndTarget?: (position: number[], target: number[]) => void;
  setCameraPosition?: (position: number[]) => void;
  setCameraTarget?: (target: number[]) => void;
  setFieldOfView?: (fov: number) => void;
  update?: () => void;
  eye?: unknown;
  target?: unknown;
};

type CadworkEngine = {
  getModule?: (name: string) => {
    getCamera?: () => CadworkCamera | undefined;
    getActiveView?: () => { getCamera?: () => CadworkCamera | undefined; viewpointChanged?: () => void } | undefined;
    scheduleRedraw?: () => void;
  } | undefined;
};

export function clonePose(pose: HtmlViewerCameraPose): HtmlViewerCameraPose {
  return {
    position: { ...pose.position },
    target: { ...pose.target },
    ...(typeof pose.zoom === 'number' ? { zoom: pose.zoom } : {}),
    ...(typeof pose.fov === 'number' ? { fov: pose.fov } : {}),
  };
}

export function vec3FromUnknown(v: unknown): HtmlViewerVec3 | null {
  if (!v || typeof v !== 'object') return null;
  if (Array.isArray(v) || (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(v))) {
    const arr = v as ArrayLike<number>;
    if (arr.length >= 3 && typeof arr[0] === 'number') {
      return { x: arr[0], y: arr[1], z: arr[2] };
    }
    return null;
  }
  const o = v as Record<string, unknown>;
  if (typeof o.x === 'number' && typeof o.y === 'number' && typeof o.z === 'number') {
    return { x: o.x, y: o.y, z: o.z };
  }
  return null;
}

export function posesNearlyEqual(a: HtmlViewerCameraPose, b: HtmlViewerCameraPose, eps = 1e-4): boolean {
  const d = (p: HtmlViewerVec3, q: HtmlViewerVec3) =>
    Math.abs(p.x - q.x) <= eps && Math.abs(p.y - q.y) <= eps && Math.abs(p.z - q.z) <= eps;
  if (!d(a.position, b.position) || !d(a.target, b.target)) return false;
  if (a.zoom != null && b.zoom != null && Math.abs(a.zoom - b.zoom) > eps) return false;
  if (a.fov != null && b.fov != null && Math.abs(a.fov - b.fov) > eps) return false;
  return true;
}

/** Skip applying a pose we just wrote (prevents left/right change listeners from looping). */
export function shouldApplyRemotePose(
  lastApplied: HtmlViewerCameraPose | null,
  incoming: HtmlViewerCameraPose,
): boolean {
  if (!lastApplied) return true;
  return !posesNearlyEqual(lastApplied, incoming);
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function angleDelta(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return Math.abs(d);
}

export function orbitFromPose(pose: HtmlViewerCameraPose, up: 'y' | 'z' = 'y'): HtmlViewerOrbit {
  const dx = pose.position.x - pose.target.x;
  const dy = pose.position.y - pose.target.y;
  const dz = pose.position.z - pose.target.z;
  const distance = Math.hypot(dx, dy, dz) || 1;
  const yaw = up === 'z' ? Math.atan2(dx, dy) : Math.atan2(dx, dz);
  const elev = up === 'z' ? dz / distance : dy / distance;
  return {
    yaw,
    pitch: Math.asin(clamp(elev, -1, 1)),
    distance,
    ...(typeof pose.fov === 'number' ? { fov: pose.fov } : {}),
    ...(typeof pose.zoom === 'number' ? { zoom: pose.zoom } : {}),
  };
}

export function positionFromOrbit(
  target: HtmlViewerVec3,
  orbit: HtmlViewerOrbit,
  up: 'y' | 'z' = 'y',
): HtmlViewerVec3 {
  const pitch = clamp(orbit.pitch, -Math.PI / 2 + 1e-4, Math.PI / 2 - 1e-4);
  const cos = Math.cos(pitch);
  const along = Math.cos(orbit.yaw) * cos * orbit.distance;
  const side = Math.sin(orbit.yaw) * cos * orbit.distance;
  const elev = Math.sin(pitch) * orbit.distance;
  if (up === 'z') {
    return { x: target.x + side, y: target.y + along, z: target.z + elev };
  }
  return { x: target.x + side, y: target.y + elev, z: target.z + along };
}

export function orbitsNearlyEqual(a: HtmlViewerOrbit, b: HtmlViewerOrbit): boolean {
  if (angleDelta(a.yaw, b.yaw) > 0.004) return false;
  if (Math.abs(a.pitch - b.pitch) > 0.004) return false;
  if (Math.abs(a.distance - b.distance) / Math.max(a.distance, b.distance, 1) > 0.008) return false;
  if (a.fov != null && b.fov != null && Math.abs(a.fov - b.fov) > 0.15) return false;
  if (a.zoom != null && b.zoom != null && Math.abs(a.zoom - b.zoom) > 1e-3) return false;
  return true;
}

export function shouldApplyRemoteOrbit(
  lastApplied: HtmlViewerOrbit | null,
  incoming: HtmlViewerOrbit,
): boolean {
  if (!lastApplied) return true;
  return !orbitsNearlyEqual(lastApplied, incoming);
}

export type HtmlViewerSyncSide = 'left' | 'right';

function orbitMoved(
  previous: HtmlViewerOrbit | null,
  current: HtmlViewerOrbit | null,
  lastApplied: HtmlViewerOrbit | null,
): boolean {
  if (!previous || !current) return false;
  if (orbitsNearlyEqual(previous, current)) return false;
  if (lastApplied && orbitsNearlyEqual(lastApplied, current)) return false;
  return true;
}

function orbitChangeAmount(from: HtmlViewerOrbit, to: HtmlViewerOrbit): number {
  return angleDelta(from.yaw, to.yaw)
    + Math.abs(from.pitch - to.pitch)
    + Math.abs(from.distance - to.distance) / Math.max(from.distance, to.distance, 1);
}

/** Decide which pane led this frame. Pointer-down on a pane wins so the other side cannot stomp it. */
export function nextHtmlViewerSyncLeader(args: {
  left: HtmlViewerOrbit | null;
  right: HtmlViewerOrbit | null;
  lastReadLeft: HtmlViewerOrbit | null;
  lastReadRight: HtmlViewerOrbit | null;
  lastAppliedToLeft: HtmlViewerOrbit | null;
  lastAppliedToRight: HtmlViewerOrbit | null;
  pointerLead?: HtmlViewerSyncSide | null;
}): HtmlViewerSyncSide | null {
  const leftMoved = orbitMoved(args.lastReadLeft, args.left, args.lastAppliedToLeft);
  const rightMoved = orbitMoved(args.lastReadRight, args.right, args.lastAppliedToRight);

  if (args.pointerLead === 'left') return leftMoved ? 'left' : null;
  if (args.pointerLead === 'right') return rightMoved ? 'right' : null;

  if (leftMoved && rightMoved && args.left && args.right && args.lastReadLeft && args.lastReadRight) {
    const l = orbitChangeAmount(args.lastReadLeft, args.left);
    const r = orbitChangeAmount(args.lastReadRight, args.right);
    return r > l ? 'right' : 'left';
  }
  if (leftMoved) return 'left';
  if (rightMoved) return 'right';
  return null;
}

function looksLikeVec3(v: unknown): v is Vec3Like {
  return !!vec3FromUnknown(v);
}

function looksLikeCamera(v: unknown): v is NonNullable<HtmlViewerCameraBinding['camera']> {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  if (!looksLikeVec3(o.position)) return false;
  return o.isCamera === true || o.isPerspectiveCamera === true || o.isOrthographicCamera === true || looksLikeVec3(o.up);
}

function looksLikeControls(v: unknown): v is NonNullable<HtmlViewerCameraBinding['controls']> {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return looksLikeVec3(o.target) && (typeof o.update === 'function' || typeof o.addEventListener === 'function');
}

function pairFrom(camera: unknown, controls: unknown): HtmlViewerCameraBinding | null {
  if (looksLikeCamera(camera) && looksLikeControls(controls)) {
    return { camera, controls, up: 'y' };
  }
  return null;
}

const COMMON_CAMERA_KEYS = ['camera', 'cam', 'perspectiveCamera'];
const COMMON_CONTROL_KEYS = ['controls', 'orbitControls', 'orbit_controls', 'controller'];

function pairFromRecord(obj: Record<string, unknown>): HtmlViewerCameraBinding | null {
  let camera: unknown;
  let controls: unknown;
  for (const key of COMMON_CAMERA_KEYS) {
    if (obj[key] != null) {
      camera = obj[key];
      break;
    }
  }
  for (const key of COMMON_CONTROL_KEYS) {
    if (obj[key] != null) {
      controls = obj[key];
      break;
    }
  }
  const direct = pairFrom(camera, controls);
  if (direct) return direct;
  const controlsObject = looksLikeControls(controls)
    ? (controls as { object?: unknown }).object
    : undefined;
  if (looksLikeControls(controls) && looksLikeCamera(controlsObject)) {
    return { camera: controlsObject, controls, up: 'y' };
  }
  const recordObject = obj.object;
  if (looksLikeControls(obj) && looksLikeCamera(recordObject)) {
    return { camera: recordObject, controls: obj, up: 'y' };
  }
  return null;
}

function walkForBinding(root: unknown, depth: number, seen: WeakSet<object>): HtmlViewerCameraBinding | null {
  if (!root || typeof root !== 'object' || depth < 0) return null;
  if (seen.has(root as object)) return null;
  seen.add(root as object);
  const rec = root as Record<string, unknown>;
  const found = pairFromRecord(rec);
  if (found) return found;
  const nestedKeys = ['viewer', 'app', 'view', 'three', 'scene3d', 'modelViewer', 'context', 'scene', 'world'];
  for (const key of nestedKeys) {
    const next = walkForBinding(rec[key], depth - 1, seen);
    if (next) return next;
  }
  return null;
}

function looksLikeCadworkCamera(v: unknown): v is CadworkCamera {
  if (!v || typeof v !== 'object') return false;
  const o = v as CadworkCamera;
  return typeof o.getCameraPosition === 'function'
    && (typeof o.getCameraTarget === 'function' || typeof o.setCameraPositionAndTarget === 'function');
}

function cameraFromEngine(engine: CadworkEngine | null | undefined): { camera: CadworkCamera; engine: CadworkEngine; view?: { viewpointChanged?: () => void } } | null {
  if (!engine || typeof engine.getModule !== 'function') return null;
  try {
    const controls = engine.getModule('viewControls');
    const fromControls = controls?.getCamera?.();
    if (looksLikeCadworkCamera(fromControls)) {
      return { camera: fromControls, engine, view: engine.getModule('viewMode')?.getActiveView?.() };
    }
  } catch {
    /* ignore */
  }
  try {
    const view = engine.getModule('viewMode')?.getActiveView?.();
    const fromView = view?.getCamera?.();
    if (looksLikeCadworkCamera(fromView)) {
      return { camera: fromView, engine, view };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function vueFromEl(el: Element | null): unknown {
  if (!el) return null;
  const rec = el as unknown as { __vue__?: unknown; __vueParentComponent?: { proxy?: unknown } };
  return rec.__vue__ || rec.__vueParentComponent?.proxy || null;
}

function cameraFromVue(root: unknown): ReturnType<typeof cameraFromEngine> {
  const seen = new WeakSet<object>();
  const walk = (node: unknown, depth: number): ReturnType<typeof cameraFromEngine> => {
    if (!node || typeof node !== 'object' || depth < 0 || seen.has(node as object)) return null;
    seen.add(node as object);
    const rec = node as Record<string, unknown>;
    const engine = (rec.engine || rec._engine || (typeof rec.getEngine === 'function' ? rec.getEngine() : null)) as CadworkEngine | undefined;
    const found = cameraFromEngine(engine);
    if (found) return found;
    const kids = rec.$children;
    if (Array.isArray(kids)) {
      for (const kid of kids) {
        const next = walk(kid, depth - 1);
        if (next) return next;
      }
    }
    return walk(rec.$parent, depth - 1) || walk(rec.$root, depth - 1);
  };
  return walk(root, 8);
}

function cadworkUp(camera: CadworkCamera): 'y' | 'z' {
  const up = vec3FromUnknown(camera.getCameraUpVector?.() ?? camera.getCameraUpVectorRef?.());
  if (!up) return 'z';
  return Math.abs(up.z) >= Math.abs(up.y) ? 'z' : 'y';
}

function toArray(v: HtmlViewerVec3): number[] {
  return [v.x, v.y, v.z];
}

function bindingFromCadwork(
  camera: CadworkCamera,
  engine: CadworkEngine,
  view?: { viewpointChanged?: () => void },
): HtmlViewerCameraBinding {
  const readPose = (): HtmlViewerCameraPose | null => {
    const position = vec3FromUnknown(camera.getCameraPosition?.() ?? camera.eye);
    const target = vec3FromUnknown(camera.getCameraTarget?.() ?? camera.target);
    if (!position || !target) return null;
    const fov = typeof camera.getFieldOfView === 'function' ? camera.getFieldOfView() : undefined;
    return {
      position,
      target,
      ...(typeof fov === 'number' && Number.isFinite(fov) ? { fov } : {}),
    };
  };
  const applyPose = (pose: HtmlViewerCameraPose) => {
    if (typeof camera.setCameraPositionAndTarget === 'function') {
      camera.setCameraPositionAndTarget(toArray(pose.position), toArray(pose.target));
    } else {
      camera.setCameraPosition?.(toArray(pose.position));
      camera.setCameraTarget?.(toArray(pose.target));
    }
    if (typeof pose.fov === 'number' && typeof camera.setFieldOfView === 'function') {
      camera.setFieldOfView(pose.fov);
    }
    camera.update?.();
    view?.viewpointChanged?.();
    engine.getModule?.('viewMode')?.scheduleRedraw?.();
  };
  return { readPose, applyPose, up: cadworkUp(camera) };
}

export function probeCadworkCamera(win: Window): HtmlViewerCameraBinding | null {
  try {
    const doc = win.document;
    if (!doc) return null;
    const canvas = doc.getElementById('canvas3d') || doc.querySelector('canvas');
    const roots = [
      vueFromEl(canvas),
      vueFromEl(doc.getElementById('viewer')),
      vueFromEl(canvas?.parentElement || null),
    ];
    let el: HTMLElement | null = canvas as HTMLElement | null;
    for (let i = 0; i < 8 && el; i += 1) {
      roots.push(vueFromEl(el));
      el = el.parentElement;
    }
    for (const vue of roots) {
      const found = cameraFromVue(vue);
      if (found) return bindingFromCadwork(found.camera, found.engine, found.view);
    }
  } catch {
    return null;
  }
  return null;
}

export function probeHtmlViewerCamera(win: Window): HtmlViewerCameraBinding | null {
  try {
    const cadwork = typeof win.document !== 'undefined' ? probeCadworkCamera(win) : null;
    if (cadwork) return cadwork;
    return walkForBinding(win, 2, new WeakSet());
  } catch {
    return null;
  }
}

export function readPoseFromBinding(binding: HtmlViewerCameraBinding): HtmlViewerCameraPose | null {
  if (binding.readPose) return binding.readPose();
  const { camera, controls } = binding;
  if (!camera || !controls) return null;
  if (!looksLikeVec3(camera.position) || !looksLikeVec3(controls.target)) return null;
  return {
    position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
    target: { x: controls.target.x, y: controls.target.y, z: controls.target.z },
    ...(typeof camera.zoom === 'number' ? { zoom: camera.zoom } : {}),
  };
}

export function applyPoseToBinding(binding: HtmlViewerCameraBinding, pose: HtmlViewerCameraPose): void {
  if (binding.applyPose) {
    binding.applyPose(pose);
    return;
  }
  const { camera, controls } = binding;
  if (!camera || !controls) return;
  if (typeof camera.position.set === 'function') {
    camera.position.set(pose.position.x, pose.position.y, pose.position.z);
  } else {
    camera.position.x = pose.position.x;
    camera.position.y = pose.position.y;
    camera.position.z = pose.position.z;
  }
  if (typeof controls.target.set === 'function') {
    controls.target.set(pose.target.x, pose.target.y, pose.target.z);
  } else {
    controls.target.x = pose.target.x;
    controls.target.y = pose.target.y;
    controls.target.z = pose.target.z;
  }
  if (typeof pose.zoom === 'number' && 'zoom' in camera) {
    camera.zoom = pose.zoom;
    camera.updateProjectionMatrix?.();
  }
  controls.update?.();
}

export function applyPoseToIframe(iframe: HTMLIFrameElement, pose: HtmlViewerCameraPose): boolean {
  const win = iframe.contentWindow;
  if (!win) return false;
  const binding = probeHtmlViewerCamera(win);
  if (!binding) return false;
  applyPoseToBinding(binding, pose);
  return true;
}

export function readPoseFromIframe(iframe: HTMLIFrameElement): HtmlViewerCameraPose | null {
  const win = iframe.contentWindow;
  if (!win) return null;
  const binding = probeHtmlViewerCamera(win);
  return binding ? readPoseFromBinding(binding) : null;
}

export function applyOrbitToIframe(iframe: HTMLIFrameElement, source: HtmlViewerCameraPose): boolean {
  const win = iframe.contentWindow;
  if (!win) return false;
  const binding = probeHtmlViewerCamera(win);
  if (!binding) return false;
  const destPose = readPoseFromBinding(binding);
  const destUp = binding.up || 'y';
  const sourceUp = destUp;
  const incoming = orbitFromPose(source, sourceUp);
  const destTarget = destPose?.target || source.target;
  const next: HtmlViewerCameraPose = {
    position: positionFromOrbit(destTarget, incoming, destUp),
    target: destTarget,
    ...(incoming.fov != null ? { fov: incoming.fov } : {}),
    ...(incoming.zoom != null ? { zoom: incoming.zoom } : {}),
  };
  applyPoseToBinding(binding, next);
  return true;
}

export function subscribeHtmlViewerCamera(
  iframe: HTMLIFrameElement,
  onPose: (pose: HtmlViewerCameraPose) => void,
): () => void {
  let binding: HtmlViewerCameraBinding | null = null;
  let cleaned = false;
  let onChange: (() => void) | null = null;
  let dragging = false;
  let raf = 0;
  const timers: number[] = [];
  let canvas: HTMLCanvasElement | null = null;
  let onPointerDown: (() => void) | null = null;
  let onPointerUp: (() => void) | null = null;

  const emit = () => {
    if (!binding) return;
    const pose = readPoseFromBinding(binding);
    if (pose) onPose(pose);
  };

  const attach = () => {
    if (cleaned || binding) return;
    const win = iframe.contentWindow;
    if (!win) return;
    const next = probeHtmlViewerCamera(win);
    if (!next) return;
    binding = next;
    onChange = emit;
    next.controls?.addEventListener?.('change', emit);
    canvas = (iframe.contentDocument?.getElementById('canvas3d') as HTMLCanvasElement | null)
      || iframe.contentDocument?.querySelector('canvas')
      || null;
    const listenOn = iframe.contentDocument || canvas;
    if (listenOn) {
      onPointerDown = () => {
        dragging = true;
        const loop = () => {
          if (!dragging || cleaned) return;
          emit();
          raf = window.requestAnimationFrame(loop);
        };
        loop();
      };
      onPointerUp = () => {
        dragging = false;
        if (raf) window.cancelAnimationFrame(raf);
        raf = 0;
        emit();
      };
      listenOn.addEventListener('pointerdown', onPointerDown);
      win.addEventListener('pointerup', onPointerUp);
    }
  };

  const onLoad = () => {
    attach();
    for (let ms = 200; ms <= 20000; ms += 400) {
      timers.push(window.setTimeout(attach, ms));
    }
  };

  iframe.addEventListener('load', onLoad);
  attach();

  return () => {
    cleaned = true;
    dragging = false;
    iframe.removeEventListener('load', onLoad);
    timers.forEach(id => window.clearTimeout(id));
    if (raf) window.cancelAnimationFrame(raf);
    if (binding && onChange) binding.controls?.removeEventListener?.('change', onChange);
    const listenOn = iframe.contentDocument || canvas;
    if (listenOn && onPointerDown) listenOn.removeEventListener('pointerdown', onPointerDown);
    const win = iframe.contentWindow;
    if (win && onPointerUp) win.removeEventListener('pointerup', onPointerUp);
  };
}

function orbitFromIframe(iframe: HTMLIFrameElement, pose: HtmlViewerCameraPose): HtmlViewerOrbit {
  const win = iframe.contentWindow;
  const up = win ? probeHtmlViewerCamera(win)?.up || 'y' : 'y';
  return orbitFromPose(pose, up);
}

function watchIframePointer(
  iframe: HTMLIFrameElement,
  onLead: (down: boolean) => void,
): () => void {
  const onDown = () => onLead(true);
  const onUp = () => onLead(false);
  const attach = () => {
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.addEventListener('pointerdown', onDown, true);
    doc.addEventListener('pointerup', onUp, true);
  };
  iframe.addEventListener('load', attach);
  attach();
  window.addEventListener('pointerup', onUp);
  return () => {
    iframe.removeEventListener('load', attach);
    iframe.contentDocument?.removeEventListener('pointerdown', onDown, true);
    iframe.contentDocument?.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('pointerup', onUp);
  };
}

/** Bidirectional orbit/zoom sync. The pane the user is dragging leads; the other follows. */
export function useHtmlViewerCameraSync(
  leftIframe: HTMLIFrameElement | null,
  rightIframe: HTMLIFrameElement | null,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled || !leftIframe || !rightIframe) return;

    let lastReadLeft: HtmlViewerOrbit | null = null;
    let lastReadRight: HtmlViewerOrbit | null = null;
    let lastAppliedToLeft: HtmlViewerOrbit | null = null;
    let lastAppliedToRight: HtmlViewerOrbit | null = null;
    let pointerLead: HtmlViewerSyncSide | null = null;
    let seeded = false;
    let raf = 0;
    let cleaned = false;

    const tick = () => {
      if (cleaned) return;
      const leftPose = readPoseFromIframe(leftIframe);
      const rightPose = readPoseFromIframe(rightIframe);
      const left = leftPose ? orbitFromIframe(leftIframe, leftPose) : null;
      const right = rightPose ? orbitFromIframe(rightIframe, rightPose) : null;

      if (!seeded && leftPose && right) {
        applyOrbitToIframe(rightIframe, leftPose);
        lastAppliedToRight = left;
        lastReadLeft = left;
        lastReadRight = left;
        seeded = true;
        raf = window.requestAnimationFrame(tick);
        return;
      }

      const leader = nextHtmlViewerSyncLeader({
        left,
        right,
        lastReadLeft,
        lastReadRight,
        lastAppliedToLeft,
        lastAppliedToRight,
        pointerLead,
      });

      if (leader === 'left' && leftPose && left) {
        applyOrbitToIframe(rightIframe, leftPose);
        lastAppliedToRight = left;
        lastReadLeft = left;
        lastReadRight = left;
      } else if (leader === 'right' && rightPose && right) {
        applyOrbitToIframe(leftIframe, rightPose);
        lastAppliedToLeft = right;
        lastReadLeft = right;
        lastReadRight = right;
      } else {
        if (left) lastReadLeft = left;
        if (right) lastReadRight = right;
      }

      raf = window.requestAnimationFrame(tick);
    };

    const unsubLeftPtr = watchIframePointer(leftIframe, down => {
      pointerLead = down ? 'left' : pointerLead === 'left' ? null : pointerLead;
    });
    const unsubRightPtr = watchIframePointer(rightIframe, down => {
      pointerLead = down ? 'right' : pointerLead === 'right' ? null : pointerLead;
    });

    tick();

    return () => {
      cleaned = true;
      if (raf) window.cancelAnimationFrame(raf);
      unsubLeftPtr();
      unsubRightPtr();
    };
  }, [leftIframe, rightIframe, enabled]);
}
